import argon2 from 'argon2-browser/dist/argon2-bundled.min';

import {
  AUTOTUNE_DEFAULT_MEMORY_MIB,
  AUTOTUNE_MAX_ITERATIONS,
  AUTOTUNE_MAX_MEMORY_MIB,
  AUTOTUNE_PARALLELISM_DESKTOP,
  AUTOTUNE_PARALLELISM_MOBILE,
  AUTOTUNE_TARGET_DESKTOP_MS,
  AUTOTUNE_TARGET_MOBILE_MS,
} from '../../constants';

import type { Argon2Profile, DeriveResult } from './types';

// Import argon2-browser - this will be provided by the client package

const { ArgonType, hash } = argon2;

/**
 * Detect if running on a mobile device
 */
function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Windows Phone/i.test(ua);
}

// Note: This implementation intentionally avoids any browser persistence
// (e.g., localStorage). Profiles are computed per-session with no caching.

/**
 * Run Argon2id with given profile
 */
async function runArgon2(
  password: Uint8Array,
  salt: Uint8Array,
  profile: Argon2Profile,
): Promise<Uint8Array> {
  const result = await hash({
    pass: password,
    salt,
    type: ArgonType.Argon2id,
    time: profile.timeCost,
    mem: profile.memoryMiB * 1024, // Convert MiB to KiB
    parallelism: profile.parallelism,
    hashLen: 32,
    outputType: 'binary',
  });

  if (!(result && result.hash instanceof Uint8Array)) {
    throw new Error('Unexpected Argon2 output format');
  }

  return result.hash;
}

/**
 * Resolve initial profile based on device type and memory hint
 */
function resolveInitialProfile(minMemoryHint: number): Argon2Profile {
  const mobile = isMobileDevice();
  const parallelism = mobile ? AUTOTUNE_PARALLELISM_MOBILE : AUTOTUNE_PARALLELISM_DESKTOP;
  const baseMemory = Math.max(minMemoryHint, AUTOTUNE_DEFAULT_MEMORY_MIB);
  return {
    memoryMiB: baseMemory,
    timeCost: 1,
    parallelism,
  };
}

/**
 * Adjust profile to increase work factor
 */
function adjustProfile(profile: Argon2Profile): Argon2Profile {
  if (profile.memoryMiB < AUTOTUNE_MAX_MEMORY_MIB) {
    return {
      ...profile,
      memoryMiB: Math.min(profile.memoryMiB * 2, AUTOTUNE_MAX_MEMORY_MIB),
    };
  }
  return {
    ...profile,
    timeCost: profile.timeCost + 1,
  };
}

/**
 * Browser implementation of Argon2id derivation with auto-tuning
 *
 * Uses argon2-browser (WASM) for cryptographic operations.
 * Supports auto-tuning to target specific execution times on different devices.
 *
 * @param password - Password bytes
 * @param salt - Salt bytes
 * @param minMemoryMiB - Minimum memory requirement
 * @param fixedProfile - Optional fixed profile (disables auto-tuning)
 * @returns Hash, profile used, and duration
 */
/**
 * Add constant-time padding to prevent timing side channels
 */
async function constantTimePadding(
  actualDurationMs: number,
  targetDurationMs: number,
): Promise<void> {
  const remainingMs = Math.max(0, targetDurationMs - actualDurationMs);
  if (remainingMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, remainingMs));
  }
}

export async function deriveArgon2id(
  password: Uint8Array,
  salt: Uint8Array,
  minMemoryMiB: number,
  fixedProfile?: Argon2Profile,
): Promise<DeriveResult> {
  const targetMs = isMobileDevice()
    ? AUTOTUNE_TARGET_MOBILE_MS
    : AUTOTUNE_TARGET_DESKTOP_MS;

  const overallStart = performance.now();

  // Fixed profile: no auto-tuning
  if (fixedProfile) {
    const hash = await runArgon2(password, salt, fixedProfile);

    // Add constant-time padding to always take target duration
    const elapsed = performance.now() - overallStart;
    await constantTimePadding(elapsed, targetMs);

    return {
      hash,
      profile: fixedProfile,
      durationMs: performance.now() - overallStart,
    };
  }

  // Auto-tuning mode (no persistence)
  const profile = resolveInitialProfile(minMemoryMiB);

  let currentProfile = { ...profile };
  let iterations = 0;
  let finalHash: Uint8Array | null = null;
  let finalProfile: Argon2Profile | null = null;

  while (iterations < AUTOTUNE_MAX_ITERATIONS) {
    iterations += 1;
    const start = performance.now();
    try {
      const hash = await runArgon2(password, salt, currentProfile);
      const duration = performance.now() - start;

      // Check if we've reached target duration or limits
      if (
        duration >= targetMs * 0.85 ||
        currentProfile.timeCost >= 6 ||
        iterations >= AUTOTUNE_MAX_ITERATIONS
      ) {
        finalHash = hash;
        finalProfile = currentProfile;
        break;
      }

      // Increase work factor
      currentProfile = adjustProfile(currentProfile);
    } catch (error) {
      console.error('Argon2id derivation failed; reducing parameters', error);

      // Try reducing parameters on failure
      if (currentProfile.timeCost > 1) {
        currentProfile = { ...currentProfile, timeCost: currentProfile.timeCost - 1 };
        continue;
      }
      if (currentProfile.memoryMiB > minMemoryMiB) {
        currentProfile = {
          ...currentProfile,
          memoryMiB: Math.max(minMemoryMiB, Math.floor(currentProfile.memoryMiB / 2)),
        };
        continue;
      }
      throw error;
    }
  }

  if (!finalHash || !finalProfile) {
    throw new Error(
      `Argon2 autotuning failed to converge after ${AUTOTUNE_MAX_ITERATIONS} iterations`,
    );
  }

  // Add constant-time padding to always take target duration
  const elapsed = performance.now() - overallStart;
  await constantTimePadding(elapsed, targetMs);

  return {
    hash: finalHash,
    profile: finalProfile,
    durationMs: performance.now() - overallStart,
  };
}
